// src/features/players/usePlayerOverlayMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { webApi } from '@/shared/api/WebApi';
import { S3FileUploader } from '@/features/upload/S3FileUploader';
import { ensureValidCredentials } from '@/features/upload/useUploadCredentials';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PLAYER_QUERY_KEYS } from '@/lib/constants/player';
import type { ClientIngest } from '@/lib/types/Upload';

interface OverlayIngestRequest {
  downloadUrl: string | null;
  filename: string;
  ingestMethodType: 'dashboard';
}

interface UploadOverlayParams {
  file: File;
  contentType: string;
}

/**
 * Upload a player overlay image: create ingest record -> upload to S3.
 * The backend creates an ingest job that can be tracked via transPlayerOverlayJobs.
 */
export function useUploadOverlay(playerId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, contentType }: UploadOverlayParams) => {
      if (!selectedCId) throw new Error('No customer selected');

      const req: OverlayIngestRequest = {
        downloadUrl: null,
        filename: file.name,
        ingestMethodType: 'dashboard',
      };

      // 1. Create overlay ingest record -> get S3 key
      const clientIngest = await webApi.post<ClientIngest>(
        `player/${playerId}/ingest-player-overlay`,
        { cId: selectedCId },
        req,
      );

      if (!clientIngest.dashboard) {
        throw new Error('Backend returned no dashboard ingest data');
      }

      const { key, ingestJobId } = clientIngest.dashboard;

      // 2. Upload file to S3
      const credentials = await ensureValidCredentials(selectedCId);
      await S3FileUploader.putObject(credentials, key, file, contentType);

      return ingestJobId;
    },
    onSuccess: () => {
      if (!selectedCId) return;
      // Invalidate player detail so overlay fields refresh after processing completes
      queryClient.invalidateQueries({ queryKey: PLAYER_QUERY_KEYS.detail(selectedCId, playerId) });
    },
  });
}

/**
 * Delete a player overlay image.
 */
export function useDeleteOverlay(playerId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!selectedCId) throw new Error('No customer selected');
      await webApi.delete(`player/${playerId}/delete-player-overlay`, { cId: selectedCId });
    },
    onSuccess: () => {
      if (!selectedCId) return;
      queryClient.invalidateQueries({ queryKey: PLAYER_QUERY_KEYS.detail(selectedCId, playerId) });
    },
  });
}
